// ============================================================================
// MODULE SERVICE
// ============================================================================
// API calls for IAM Module / Menu / Screen / Element CRUD

import { get, post, put, del } from '../client/axiosClient';
import { moduleEndpoints, screenEndpoints, menuScreenEndpoints } from '../endpoints/iam.endpoints';
import { buildEndpointWithQuery } from '../endpoints/base.endpoints';
import type {
  ApiResponse,
  BackendPagedResult,
  PaginatedResponse,
  EmptyResponse,
  ID,
} from '../types';
import type {
  ModuleDto,
  CreateModuleRequest,
  UpdateModuleRequest,
  MenuDto,
  CreateMenuRequest,
  UpdateMenuRequest,
  MenuScreenDto,
  LinkScreenToMenuRequest,
  ScreenDto,
  CreateScreenRequest,
  UpdateScreenRequest,
  ScreenElementDto,
  CreateScreenElementRequest,
  UpdateScreenElementRequest,
} from '../types/iam.types';

class ModuleService {
  // --------------------------------------------------------------------------
  // MODULES
  // --------------------------------------------------------------------------
  async getAllModules(
    params?: { page?: number; pageSize?: number; searchTerm?: string; includeInactive?: boolean }
  ): Promise<PaginatedResponse<ModuleDto>> {
    const endpoint = params
      ? buildEndpointWithQuery(moduleEndpoints.list(), params)
      : moduleEndpoints.list();
    const response = await get<ApiResponse<BackendPagedResult<ModuleDto>>>(endpoint);
    const paged = response.data.data;
    return {
      success: response.data.success,
      message: response.data.message,
      data: Array.from(paged.items || []),
      pagination: {
        currentPage: paged.page,
        pageSize: paged.pageSize,
        totalPages: paged.totalPages,
        totalItems: paged.totalCount,
        hasNextPage: paged.hasNext,
        hasPreviousPage: paged.hasPrevious,
      },
    };
  }

  async getModuleById(id: ID): Promise<ApiResponse<ModuleDto>> {
    const response = await get<ApiResponse<ModuleDto>>(moduleEndpoints.getById(id));
    return response.data;
  }

  async createModule(data: CreateModuleRequest): Promise<ApiResponse<ModuleDto>> {
    const response = await post<ApiResponse<ModuleDto>>(moduleEndpoints.create(), data);
    return response.data;
  }

  async updateModule(id: ID, data: UpdateModuleRequest): Promise<ApiResponse<ModuleDto>> {
    const response = await put<ApiResponse<ModuleDto>>(moduleEndpoints.update(id), data);
    return response.data;
  }

  async deleteModule(id: ID): Promise<EmptyResponse> {
    const response = await del<EmptyResponse>(moduleEndpoints.delete(id));
    return response.data;
  }

  async isModuleCodeAvailable(code: string, excludeId?: ID): Promise<boolean> {
    const response = await get<ApiResponse<boolean>>(
      moduleEndpoints.validateCode(code, excludeId)
    );
    return response.data.data;
  }

  // --------------------------------------------------------------------------
  // MENUS
  // --------------------------------------------------------------------------
  async getMenusByModule(moduleId: ID): Promise<ApiResponse<MenuDto[]>> {
    const response = await get<ApiResponse<BackendPagedResult<MenuDto>>>(moduleEndpoints.listMenus(moduleId));
    const paged = response.data.data;
    return {
      success: response.data.success,
      message: response.data.message,
      data: Array.from(paged.items || []),
    };
  }

  async getMenusByModulePaged(
    moduleId: ID,
    params?: { page?: number; pageSize?: number; searchTerm?: string; includeInactive?: boolean }
  ): Promise<PaginatedResponse<MenuDto>> {
    const base = moduleEndpoints.listMenus(moduleId);
    const endpoint = params ? buildEndpointWithQuery(base, params) : base;
    const response = await get<ApiResponse<BackendPagedResult<MenuDto>>>(endpoint);
    const paged = response.data.data;
    return {
      success: response.data.success,
      message: response.data.message,
      data: Array.from(paged.items || []),
      pagination: {
        currentPage: paged.page,
        pageSize: paged.pageSize,
        totalPages: paged.totalPages,
        totalItems: paged.totalCount,
        hasNextPage: paged.hasNext,
        hasPreviousPage: paged.hasPrevious,
      },
    };
  }

  async createMenu(moduleId: ID, data: CreateMenuRequest): Promise<ApiResponse<MenuDto>> {
    const response = await post<ApiResponse<MenuDto>>(
      moduleEndpoints.createMenu(moduleId),
      data
    );
    return response.data;
  }

  async updateMenu(moduleId: ID, id: ID, data: UpdateMenuRequest): Promise<ApiResponse<MenuDto>> {
    const response = await put<ApiResponse<MenuDto>>(
      moduleEndpoints.updateMenu(moduleId, id),
      data
    );
    return response.data;
  }

  async deleteMenu(moduleId: ID, id: ID): Promise<EmptyResponse> {
    const response = await del<EmptyResponse>(moduleEndpoints.deleteMenu(moduleId, id));
    return response.data;
  }

  // --------------------------------------------------------------------------
  // MENU-SCREEN LINKS
  // --------------------------------------------------------------------------
  async getScreensByMenu(menuId: ID): Promise<ApiResponse<MenuScreenDto[]>> {
    const response = await get<ApiResponse<MenuScreenDto[]>>(menuScreenEndpoints.list(menuId));
    return response.data;
  }

  async linkScreenToMenu(menuId: ID, data: LinkScreenToMenuRequest): Promise<ApiResponse<MenuScreenDto>> {
    const response = await post<ApiResponse<MenuScreenDto>>(menuScreenEndpoints.link(menuId), data);
    return response.data;
  }

  async unlinkScreenFromMenu(menuId: ID, screenId: ID): Promise<EmptyResponse> {
    const response = await del<EmptyResponse>(menuScreenEndpoints.unlink(menuId, screenId));
    return response.data;
  }

  // --------------------------------------------------------------------------
  // SCREENS
  // --------------------------------------------------------------------------
  async getAllScreens(
    params?: { page?: number; pageSize?: number; searchTerm?: string; includeInactive?: boolean }
  ): Promise<PaginatedResponse<ScreenDto>> {
    const endpoint = params
      ? buildEndpointWithQuery(screenEndpoints.list(), params)
      : screenEndpoints.list();
    const response = await get<ApiResponse<BackendPagedResult<ScreenDto>>>(endpoint);
    const paged = response.data.data;
    return {
      success: response.data.success,
      message: response.data.message,
      data: Array.from(paged.items || []),
      pagination: {
        currentPage: paged.page,
        pageSize: paged.pageSize,
        totalPages: paged.totalPages,
        totalItems: paged.totalCount,
        hasNextPage: paged.hasNext,
        hasPreviousPage: paged.hasPrevious,
      },
    };
  }

  async createScreen(data: CreateScreenRequest): Promise<ApiResponse<ScreenDto>> {
    const response = await post<ApiResponse<ScreenDto>>(screenEndpoints.create(), data);
    return response.data;
  }

  async updateScreen(id: ID, data: UpdateScreenRequest): Promise<ApiResponse<ScreenDto>> {
    const response = await put<ApiResponse<ScreenDto>>(screenEndpoints.update(id), data);
    return response.data;
  }

  async deleteScreen(id: ID): Promise<EmptyResponse> {
    const response = await del<EmptyResponse>(screenEndpoints.delete(id));
    return response.data;
  }

  // --------------------------------------------------------------------------
  // SCREEN ELEMENTS
  // --------------------------------------------------------------------------
  async getElementsByScreen(screenId: ID): Promise<ApiResponse<ScreenElementDto[]>> {
    const response = await get<ApiResponse<BackendPagedResult<ScreenElementDto>>>(
      screenEndpoints.listElements(screenId)
    );
    const paged = response.data.data;
    return {
      success: response.data.success,
      message: response.data.message,
      data: Array.from(paged.items || []),
    };
  }

  async createElement(
    screenId: ID,
    data: CreateScreenElementRequest
  ): Promise<ApiResponse<ScreenElementDto>> {
    const response = await post<ApiResponse<ScreenElementDto>>(
      screenEndpoints.createElement(screenId),
      data
    );
    return response.data;
  }

  async updateElement(
    screenId: ID,
    id: ID,
    data: UpdateScreenElementRequest
  ): Promise<ApiResponse<ScreenElementDto>> {
    const response = await put<ApiResponse<ScreenElementDto>>(
      screenEndpoints.updateElement(screenId, id),
      data
    );
    return response.data;
  }

  async deleteElement(screenId: ID, id: ID): Promise<EmptyResponse> {
    const response = await del<EmptyResponse>(screenEndpoints.deleteElement(screenId, id));
    return response.data;
  }
}

export const moduleService = new ModuleService();
export default moduleService;
